import type { OMSConfig, StrategyEvaluator, SignalResult, StrategyContext } from '../types';
import { ensureFlatOpts } from '../../analysis/trend-evaluator';
import { extractSnapshotOptions } from '../../../lib/snapshot-search';

/**
 * MarketWatchActivityShift — Activity Shift Signal
 *
 * Mirrors the logic from MarketWatchActivityHistoryTable.tsx.
 * 
 * Compares current vs previous snapshots' ITM top N strikes.
 * - Collects ALL dropped strikes (CE & PE combined).
 * - Collects ALL added strikes (CE & PE combined).
 * 
 * If droppedStrike < addedStrike -> BULLISH
 * If droppedStrike > addedStrike -> BEARISH
 */
export const evaluateMarketWatchActivityShift: StrategyEvaluator = (data: any, currentUnixTime: number, config: OMSConfig, context: StrategyContext): SignalResult | null => {
    if (!data) return null;

    const { symbol, expiry, activeRange, gap, settingsMode, baseSnapshot } = context;
    if (!baseSnapshot) return null;

    const spot = data.spotPrice || data.index_ltp || data.ltp || 0;
    if (spot <= 0) return null;
    
    const trendAnalysisResult = context.liveIndicators?.trendAnalysisData;
    const fullTrendData = Array.isArray(trendAnalysisResult) ? trendAnalysisResult : (trendAnalysisResult?.data || []);
    if (fullTrendData.length < 2) return null;

    const topN = 10; // Top 10 strikes (same as ALL in UI for ITM)

    const cellDataKeys: Record<string, string> = {
        'Combined OI': 'combOI',
        'Combined Volume': 'combVol',
        'Top OI': 'oi',
        'Top Volume': 'volume',
        'Top Chng OI': 'oiChg',
    };
    const valKey = cellDataKeys[settingsMode] || 'oi';

    // ─── Helper: Calculate ITM strikes exactly like the UI Table ───
    const calcITM = (row: any) => {
        const rawOpts = row.flatOpts || row.rawOpts || [];
        if (rawOpts.length === 0) return { validCE: new Set<number>(), validPE: new Set<number>(), ceStrikes: [], peStrikes: [] };
        
        const rowSpot = row.spotPrice || row.indexLtp || spot;
        const atmStrike = Math.round(rowSpot / gap) * gap;
        const minStrike = atmStrike - (activeRange * gap);
        const maxStrike = atmStrike + (activeRange * gap);

        // 1. Filter by range
        const inRangeRow = rawOpts.filter((opt: any) => opt.strike >= minStrike && opt.strike <= maxStrike);
        
        // 2. ITM status
        const itmOpts = inRangeRow.filter((opt: any) => {
            if (opt.strike === atmStrike) return true;
            const isCE = opt.type === 'CE';
            return isCE ? opt.strike < atmStrike : opt.strike > atmStrike;
        });
        
        const uniqueItmOpts: any[] = [];
        const seen = new Set<string>();
        for (const opt of itmOpts) {
            const key = `${opt.strike}_${opt.type}`;
            if (!seen.has(key)) {
                seen.add(key);
                uniqueItmOpts.push(opt);
            }
        }
        
        uniqueItmOpts.sort((a, b) => (Number(b[valKey]) || 0) - (Number(a[valKey]) || 0));
        const topItmOpts = uniqueItmOpts.slice(0, topN);

        const ceStrikes = topItmOpts.filter((o: any) => o.type === 'CE').map((o: any) => o.strike as number);
        const peStrikes = topItmOpts.filter((o: any) => o.type === 'PE').map((o: any) => o.strike as number);
        
        const validCE = new Set<number>(inRangeRow.filter((o: any) => o.type === 'CE').map((o: any) => o.strike));
        const validPE = new Set<number>(inRangeRow.filter((o: any) => o.type === 'PE').map((o: any) => o.strike));
        
        return { validCE, validPE, ceStrikes, peStrikes };
    };

    // ─── Current vs Previous ───
    let rowShiftSignal: 'BULL' | 'BEAR' | 'Neutral' = 'Neutral';
    let latestDropped = 0;
    let latestAdded = 0;

    // Iterate backwards through history to find the most recent persistent signal
    for (let i = 0; i < fullTrendData.length - 1; i++) {
        const current = calcITM(fullTrendData[i]);
        const previous = calcITM(fullTrendData[i + 1]);

        const currCE = new Set<number>(current.ceStrikes);
        const currPE = new Set<number>(current.peStrikes);
        const prevCE = new Set<number>(previous.ceStrikes);
        const prevPE = new Set<number>(previous.peStrikes);

        const droppedCE = Array.from(prevCE).filter(s => !currCE.has(s) && current.validCE.has(s)).sort((a, b) => b - a);
        const droppedPE = Array.from(prevPE).filter(s => !currPE.has(s) && current.validPE.has(s)).sort((a, b) => b - a);
        
        const addedCE = Array.from(currCE).filter(s => !prevCE.has(s)).sort((a, b) => b - a);
        const addedPE = Array.from(currPE).filter(s => !prevPE.has(s)).sort((a, b) => b - a);

        const allDropped = [...droppedCE, ...droppedPE];
        const allAdded = [...addedCE, ...addedPE];

        if (allDropped.length > 0 && allAdded.length > 0) {
            const droppedStrike = allDropped[0]!;
            const addedStrike = allAdded[0]!;
            
            if (droppedStrike < addedStrike) {
                rowShiftSignal = 'BULL';
            } else if (droppedStrike > addedStrike) {
                rowShiftSignal = 'BEAR';
            }
            
            if (rowShiftSignal !== 'Neutral') {
                latestDropped = droppedStrike;
                latestAdded = addedStrike;
                break; // Found the most recent persistent signal, stop looking back
            }
        }
    }

    if (rowShiftSignal === 'Neutral') return null;

    // ─── Entry Strike (ATM option) ───
    let entryPrice = 0;
    const optionType = rowShiftSignal === 'BULL' ? 'CE' : 'PE';
    
    const liveSpotPrice = fullTrendData[0]?.spotPrice || fullTrendData[0]?.indexLtp || spot;
    const liveAtmStrike = Math.round(liveSpotPrice / gap) * gap;

    const liveRawOptsArr = fullTrendData[0].flatOpts || fullTrendData[0].rawOpts || context.chain || extractSnapshotOptions(data, symbol, expiry) || [];
    const liveFlatOpts = ensureFlatOpts(liveRawOptsArr);
    
    const atmRow = liveFlatOpts.find((r: any) => r.strike === liveAtmStrike && r.type === optionType);
    if (atmRow) {
        entryPrice = atmRow.ltp || atmRow.close || atmRow.price || 0;
    }
    
    if (entryPrice <= 0) return null;

    return {
        trade_type: rowShiftSignal,
        strike: liveAtmStrike,
        option_type: optionType,
        entry_price: entryPrice,
        remarks: `${rowShiftSignal} Activity Shift (Drop:${latestDropped} | Add:${latestAdded})`
    };
};
